import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH11_QUESTIONS } from "../data/neetPhysicsCh11Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 11: Thermal Properties of Matter (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Temperature & Thermal Expansion", "Calorimetry & Specific Heat", "Change of State & Latent Heat", "Heat Transfer"];

const SECTION_COLORS = {
  "Temperature & Thermal Expansion": "#2563eb",
  "Calorimetry & Specific Heat":     "#d97706",
  "Change of State & Latent Heat":   "#dc2626",
  "Heat Transfer":                   "#0d9488",
};

const CONCEPTS = [
  // ── Temperature & Thermal Expansion ─────────────────────────────────────────
  { section: "Temperature & Thermal Expansion", title: "Temperature scales — Celsius, Fahrenheit and Kelvin",
    explanation: "Celsius fixes the ice point at 0 and the steam point at 100 (100 divisions). Fahrenheit fixes them at 32 and 212 (180 divisions). Kelvin is the absolute scale, with the ice point at 273.15 K (same 100-division size as Celsius, just shifted). Conversions: C/100 = (F-32)/180 = (K-273.15)/100, giving F = (9/5)C + 32 and K = C + 273.15. Kelvin and Celsius degrees are the same SIZE (only the zero is shifted); a Fahrenheit degree is smaller (5/9 the size of a Celsius/Kelvin degree).",
    example: `Ice point / Steam point:
Celsius:    0 C    / 100 C    (100 divisions)
Fahrenheit: 32 F   / 212 F    (180 divisions)
Kelvin:     273 K  / 373 K    (100 divisions)

Convert 37 C (body temperature) to Fahrenheit:
F = (9/5) x C + 32 = (9/5)(37) + 32 = 66.6 + 32 = 98.6 F

Convert 25 C to Kelvin:
K = C + 273 = 25 + 273 = 298 K` },
  { section: "Temperature & Thermal Expansion", title: "Linear thermal expansion (coefficient alpha)",
    explanation: "Most solids expand on heating. For a rod of original length L0 heated through a temperature rise dT, the new length is L = L0(1 + alpha.dT), where alpha (the coefficient of linear expansion) = dL / (L0.dT), with SI unit per kelvin (K^-1). Alpha is a material property, typically of order 10^-5 per K for common metals.",
    example: `L = L0 (1 + alpha . dT)      alpha = dL / (L0 . dT)

Steel rod: L0 = 2 m, alpha = 1.2 x 10^-5 /K
Heated from 20 C to 70 C  ->  dT = 50 K

dL = L0 . alpha . dT = 2 x 1.2x10^-5 x 50 = 1.2 x 10^-3 m = 1.2 mm
New length = 2.0012 m` },
  { section: "Temperature & Thermal Expansion", title: "Area and volume expansion (beta = 2alpha, gamma = 3alpha)",
    explanation: "An isotropic solid expands by the same fractional amount alpha per degree in every direction, so its area expands with coefficient beta = 2alpha (A = A0(1 + beta.dT)) and its volume expands with coefficient gamma = 3alpha (V = V0(1 + gamma.dT)). Liquids and gases have no fixed shape, so only their volume expansion coefficient gamma is meaningful.",
    example: `beta = 2 . alpha           A = A0 (1 + beta . dT)
gamma = 3 . alpha          V = V0 (1 + gamma . dT)

Aluminium sheet: alpha = 2.4 x 10^-5 /K -> beta = 4.8 x 10^-5 /K
A0 = 2 m^2, dT = 40 K
dA = A0 . beta . dT = 2 x 4.8x10^-5 x 40 = 3.84 x 10^-3 m^2` },
  { section: "Temperature & Thermal Expansion", title: "Thermal stress in a rod whose expansion is prevented",
    explanation: "If a rod is rigidly clamped at both ends so it cannot expand when heated, the expansion it is prevented from undergoing instead produces internal compressive stress. Using Young's modulus Y, this thermal stress = Y . alpha . dT (as if the rod had been forcibly compressed back by the strain alpha.dT it was denied). This is why railway tracks and bridges are built with expansion gaps.",
    example: `Thermal stress = Y . alpha . dT     (strain prevented = alpha . dT)

Steel rod, both ends fixed: Y = 2 x 10^11 N/m^2
alpha = 1.2 x 10^-5 /K, dT = 50 K (temperature rise)

Stress = 2x10^11 x 1.2x10^-5 x 50 = 1.2 x 10^8 N/m^2  (120 MPa)` },
  { section: "Temperature & Thermal Expansion", title: "Anomalous expansion of water",
    explanation: "Unlike most substances, water does not contract uniformly on cooling: from higher temperatures down to 4 C its volume decreases (density increases) as normal, but between 4 C and 0 C it actually EXPANDS again (density decreases) as it approaches freezing. Water's density is therefore maximum at 4 C, not at 0 C. This is why ice forms at the TOP of a pond first and floats, insulating the water below and letting aquatic life survive through winter.",
    example: `Density of water vs temperature (schematic):
 0C --- expands on further cooling --- 4C --- normal contraction --- 20C
(density                            (density                      (density
 rising)                             MAXIMUM)                      falling)

Consequence: ice (density ~917 kg/m^3) floats on liquid water (1000 kg/m^3)` },

  // ── Calorimetry & Specific Heat ─────────────────────────────────────────────
  { section: "Calorimetry & Specific Heat", title: "Specific heat capacity and heat capacity",
    explanation: "Specific heat capacity s of a substance is the heat needed to raise the temperature of UNIT MASS by 1 degree: s = Q/(m.dT), SI unit J/(kg.K). Heat capacity (thermal capacity) C of a particular object is the heat needed to raise that WHOLE object's temperature by 1 degree: C = m.s = Q/dT, SI unit J/K -- unlike specific heat capacity, it depends on how much material is present. Water's specific heat (4186 J/(kg.K)) is unusually high, which is why large water bodies moderate coastal climates.",
    example: `Q = m . s . dT              (s = specific heat capacity of the material)
C = m . s = Q / dT           (C = heat capacity of THIS object)

Heat 2 kg of water from 20 C to 80 C  (s_water = 4186 J/(kg.K)):
Q = 2 x 4186 x 60 = 502,320 J = 502.3 kJ` },
  { section: "Calorimetry & Specific Heat", title: "Molar specific heat capacity",
    explanation: "Molar specific heat capacity C is the heat required to raise the temperature of ONE MOLE of a substance by 1 degree: Q = n.C.dT, SI unit J/(mol.K). It relates to the specific heat capacity s via C = M.s, where M is the molar mass (kg/mol). For gases, the molar specific heat at constant pressure (Cp) is greater than at constant volume (Cv), because at constant pressure some of the supplied heat also does work in expanding the gas.",
    example: `C = M . s          (M = molar mass, s = specific heat capacity)

Water: M = 0.018 kg/mol, s = 4186 J/(kg.K)
C = 0.018 x 4186 = 75.3 J/(mol.K)   (matches the known molar heat
                                     capacity of liquid water)` },
  { section: "Calorimetry & Specific Heat", title: "Principle of calorimetry — heat lost = heat gained",
    explanation: "When two bodies at different temperatures are mixed or placed in contact inside an ISOLATED system (no heat exchange with the surroundings), heat flows from the hotter body to the colder one until both reach a common final temperature. By conservation of energy: heat lost by the hotter body = heat gained by the colder body. This 'principle of calorimetry' underlies the method of mixtures used to measure unknown specific heats.",
    example: `Heat lost by hot body = Heat gained by cold body

200 g water at 90 C mixed with 300 g water at 20 C (same substance,
so s cancels):
m1(T1 - Tf) = m2(Tf - T2)
200(90 - Tf) = 300(Tf - 20)
18000 - 200Tf = 300Tf - 6000  ->  500Tf = 24000  ->  Tf = 48 C` },
  { section: "Calorimetry & Specific Heat", title: "Calorimetry with a calorimeter — water equivalent",
    explanation: "A real calorimeter cup also absorbs some heat as its temperature changes. Its effect is accounted for using its 'water equivalent' W = (mass of calorimeter x its specific heat) / (specific heat of water) -- the mass of water that would need the same heat for the same temperature rise. For a calorimeter with negligible heat capacity (as often assumed in basic problems), the method of mixtures simplifies to just balancing the heat of the two substances mixed.",
    example: `Heat lost by metal = Heat gained by water  (calorimeter heat capacity ~ 0)

0.5 kg copper (s = 390 J/(kg.K)) at 200 C dropped into 1 kg water
(s = 4186 J/(kg.K)) at 20 C:

0.5x390x(200 - Tf) = 1x4186x(Tf - 20)
195(200 - Tf) = 4186(Tf - 20)
39000 - 195Tf = 4186Tf - 83720
122720 = 4381 Tf   ->   Tf ~ 28.0 C` },

  // ── Change of State & Latent Heat ───────────────────────────────────────────
  { section: "Change of State & Latent Heat", title: "Change of state — melting, boiling and the effect of pressure",
    explanation: "A substance changes between solid, liquid and gas at a fixed melting point / boiling point for a given pressure -- the boiling point is the temperature at which a liquid's vapour pressure equals the external pressure. Increasing external pressure RAISES the boiling point (a pressure cooker boils water above 100 C, cooking food faster; at high altitude, lower atmospheric pressure lowers the boiling point). Ice is unusual: increasing pressure LOWERS its melting point, because water (unlike most liquids) is denser than its solid form.",
    example: `Water at 1 atm: boils at 100 C, melts/freezes at 0 C

Pressure cooker (~2 atm): boiling point rises to ~120 C -> faster cooking
High mountain (lower pressure): boiling point drops to ~90 C -> food
   takes longer to cook, water "boils" at a lower temperature

Ice under high pressure (e.g. beneath a skate blade): melting point
   drops below 0 C -> a thin film of liquid water forms (regelation)` },
  { section: "Change of State & Latent Heat", title: "Latent heat of fusion",
    explanation: "Latent heat is the heat absorbed or released during a change of state at CONSTANT temperature (no temperature change while the state is changing): L = Q/m. The latent heat of FUSION, Lf, applies to the solid <-> liquid transition. For ice, Lf = 3.34 x 10^5 J/kg -- melting ice absorbs this heat while staying at 0 C throughout, and freezing water releases the same amount.",
    example: `Q = m . Lf              (Lf,ice = 3.34 x 10^5 J/kg)

Heat needed to melt 2 kg of ice already at 0 C:
Q = 2 x 3.34x10^5 = 6.68 x 10^5 J = 668 kJ
(all of this heat goes into breaking the ice's crystal structure --
 the temperature stays at 0 C the entire time)` },
  { section: "Change of State & Latent Heat", title: "Latent heat of vaporization",
    explanation: "The latent heat of VAPORIZATION, Lv, applies to the liquid <-> gas transition. For water, Lv = 2.26 x 10^6 J/kg -- roughly 7 times Lf, since vaporization must overcome essentially ALL the intermolecular attractions holding the liquid together, not just the ordered lattice structure of the solid. This is why a steam burn is far more severe than a hot-water burn of the same mass: condensing steam dumps a huge amount of extra latent heat into the skin before it even starts cooling as liquid.",
    example: `Q = m . Lv              (Lv,water = 2.26 x 10^6 J/kg)

Heat RELEASED when 5 g of steam at 100 C condenses to water at 100 C:
Q = 0.005 x 2.26x10^6 = 11,300 J = 11.3 kJ

Compare: 5 g of boiling water merely cooling by 10 C releases only
Q = m.s.dT = 0.005 x 4186 x 10 = 209 J  -- over 50x less heat` },
  { section: "Change of State & Latent Heat", title: "Heating curve for ice -> water -> steam",
    explanation: "Plotting temperature against heat supplied for a fixed mass being heated from below 0 C to steam gives a curve with two SLOPED sections (temperature rising, heat going into kinetic energy of molecules) and two FLAT sections (temperature constant at 0 C and at 100 C, heat instead going entirely into latent heat as the state changes). The flat sections are exactly where melting/vaporization happen.",
    example: `1 kg ice, -10 C -> steam at 100 C  (s_ice=2100, s_water=4186 J/(kg.K);
                                     Lf=3.34x10^5, Lv=2.26x10^6 J/kg)

Q1 (ice -10C -> 0C):   1 x 2100 x 10        =   21,000 J   (sloped)
Q2 (melt at 0C):       1 x 3.34x10^5        =  334,000 J   (FLAT)
Q3 (water 0C->100C):   1 x 4186 x 100       =  418,600 J   (sloped)
Q4 (vaporize at 100C): 1 x 2.26x10^6        = 2,260,000 J  (FLAT)
                                     Total  = 3,033,600 J  ~ 3033.6 kJ` },

  // ── Heat Transfer ────────────────────────────────────────────────────────────
  { section: "Heat Transfer", title: "Conduction and thermal conductivity (Fourier's law)",
    explanation: "Conduction transfers heat through a material via molecular collisions and free electrons, without any bulk motion of the material itself. In steady state, the rate of heat flow through a slab is H = dQ/dt = K.A.(T1-T2)/x (Fourier's law), where K = thermal conductivity of the material (W/(m.K)), A = cross-sectional area, x = thickness, and (T1-T2) = temperature difference across the slab. Metals have high K (good conductors); air, wood and glass wool have low K (good insulators).",
    example: `H = K . A . (T1 - T2) / x

Metal rod: K = 200 W/(m.K), A = 0.01 m^2, x = 2 m, (T1-T2) = 50 K
H = 200 x 0.01 x 50 / 2 = 50 W    (50 joules of heat flow every second)` },
  { section: "Heat Transfer", title: "Convection",
    explanation: "Convection transfers heat by the ACTUAL BULK MOVEMENT of a fluid (liquid or gas) that carries thermal energy with it -- unlike conduction, where the medium itself stays put. Natural convection is driven by density differences (warmer, less dense fluid rises; cooler, denser fluid sinks); forced convection is driven by an external agent like a fan or pump.",
    example: `Sea breeze (daytime): land heats up faster than the sea ->
   warm air over land rises -> cooler air flows in from the sea
Land breeze (night): land cools faster than the sea -> the
   circulation reverses

Both driven purely by density differences in heated air -- no
external pump needed (natural convection)` },
  { section: "Heat Transfer", title: "Radiation and the Stefan-Boltzmann law",
    explanation: "Radiation transfers heat via electromagnetic waves and, unlike conduction and convection, needs NO medium at all -- it is how solar energy crosses the vacuum of space to reach Earth. Every body above absolute zero radiates energy; the Stefan-Boltzmann law gives the power radiated as P = eps.sigma.A.T^4, where eps = emissivity (0 to 1; eps=1 for an ideal black body), sigma = 5.67 x 10^-8 W/(m^2.K^4) (Stefan's constant), A = surface area, and T = absolute temperature in kelvin.",
    example: `P = eps . sigma . A . T^4

Sphere of radius 0.05 m, eps = 1, T = 1000 K:
A = 4.pi.r^2 = 4 x pi x (0.05)^2 = 0.0314 m^2
P = 5.67x10^-8 x 0.0314 x (1000)^4 = 5.67x10^-8 x 0.0314 x 10^12
P ~ 1780 W

Doubling T (same A, eps): power scales as T^4 -> increases 2^4 = 16x` },
  { section: "Heat Transfer", title: "Wien's displacement law",
    explanation: "A hot body emits a whole spectrum of wavelengths, but the wavelength at which it radiates MOST INTENSELY (lambda_max) shifts to shorter wavelengths as its temperature rises -- this is Wien's displacement law: lambda_max . T = b, where b = 2.9 x 10^-3 m.K (Wien's constant). It explains why a heated object glows dull red, then orange-white, then blue-white as it gets hotter, and lets astronomers estimate a star's surface temperature just from its color.",
    example: `lambda_max . T = b = 2.9 x 10^-3 m.K

Sun's surface temperature ~ 5800 K:
lambda_max = b / T = 2.9x10^-3 / 5800 ~ 5.0 x 10^-7 m = 500 nm
(falls in the visible blue-green range, close to the Sun's
 actual peak emission wavelength)` },
  { section: "Heat Transfer", title: "Newton's law of cooling",
    explanation: "For a body cooling in surroundings at a fixed lower temperature, Newton's law of cooling says the RATE of loss of heat (and hence of temperature) is proportional to the temperature difference between the body and its surroundings, provided this difference is small: -dT/dt = k(T - T0). For a temperature drop from T1 to T2 over time t, this is often applied in the approximate form (T1-T2)/t = k[(T1+T2)/2 - T0], using the AVERAGE body temperature over the interval.",
    example: `(T1 - T2)/t = k [ (T1+T2)/2 - T0 ]

A body cools from 80 C to 70 C in 5 min; surroundings at 30 C:
(80-70)/5 = k(75 - 30)  ->  2 = 45k  ->  k = 2/45 per min

Time to further cool from 70 C to 60 C:
(70-60)/t = (2/45)(65-30) = (2/45)(35) = 1.556
t = 10/1.556 ~ 6.43 min` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh11Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh11Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh11Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH11_QUESTIONS : NEET_PHYSICS_CH11_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 11</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Thermal Properties of Matter</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH11_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH11_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
